import { z } from "zod";

import { fileSchema } from "../file";
import { topicDataSchema } from "../topic-data";

import { findParentCycle } from "./entityHierarchy";

export const CAMERA_TYPE = {
  orbit: "orbit",
  thirdPerson: "third_person",
} as const;

/**
 * A camera that orbits a tracked entity, following it as it moves.
 *
 * `target.node` narrows what is followed from the whole entity to **one node
 * inside the model it draws** — a gripper, a head, an end effector. Absent means
 * the entity itself, which is what every camera saved before this field existed
 * means and what a camera drawing something other than a model can mean at all.
 *
 * Stored as a bare node name beside `id` and `mesh` rather than as the qualified
 * `entity0.mesh.gripper_link` path the settings *display*, for two reasons. The
 * prefix is `id` and `mesh`, which this object already holds, so storing it
 * again would let the two disagree the moment `RenameThreeDEntity` re-aims the
 * camera — a stale prefix would then read as a target that no longer exists. And
 * a glTF node name may itself contain dots, so a joined string cannot be split
 * back apart without knowing where the component ends; keeping the fields apart
 * is the same answer this schema already gives for a topic and its message path.
 *
 * A name matching no node in the model is **not** an error: the model can be
 * re-picked, or its source description re-ingested with a link renamed. The
 * camera falls back to the entity's own position, and the settings say why.
 */
export const orbitCameraSchema = z.strictObject({
  type: z.literal(CAMERA_TYPE.orbit),
  target: z.object({
    id: z.string(),
    mesh: z.enum(["mesh", "path"] as const),
    node: z.string().optional(),
  }),
  zoom: z.object({
    min: z.number().optional(),
    max: z.number().optional(),
  }),
});

export type OrbitCamera = z.infer<typeof orbitCameraSchema>;

/**
 * RTS-style third-person camera that orbits a free-floating world-space
 * focus point. Decoupled from any tracked entity: the camera stays put
 * during playback, and the user moves the focus point directly via pan
 * input. Beta is clamped to `[0.01, pi - 0.01]` at runtime, and radius
 * to a **scene-derived** envelope — `computeThirdPersonZoom` recomputes
 * `[max(0.5, r * 0.1), r * 41]` from the union of the entities' bounding
 * boxes each frame, where `r` is that union's bounding radius, falling
 * back to `[0.5, 41.5]` for an empty scene. (This comment previously
 * described a hardcoded `[2, 100]`, which predates ENG-2233; a fixed
 * envelope is what made small scenes unzoomable and inflated the draw
 * distance for large ones.)
 *
 * Numeric fields use `.finite()` to reject `NaN` and `±Infinity`, which
 * would propagate through the view-matrix math and corrupt rendering.
 * Out-of-envelope finite values are accepted at the schema layer and
 * clamped at runtime by `constrainCamera` (radius) and Babylon's
 * `lowerBetaLimit`/`upperBetaLimit` (beta).
 */
export const thirdPersonCameraSchema = z.strictObject({
  type: z.literal(CAMERA_TYPE.thirdPerson),
  focusPoint: z.strictObject({
    x: z.number().finite(),
    y: z.number().finite(),
    z: z.number().finite(),
  }),
  alpha: z.number().finite(),
  beta: z.number().finite(),
  radius: z.number().finite(),
});

export type ThirdPersonCamera = z.infer<typeof thirdPersonCameraSchema>;

export const cameraSchema = z.discriminatedUnion("type", [
  orbitCameraSchema,
  thirdPersonCameraSchema,
]);

export type Camera = z.infer<typeof cameraSchema>;

export const pathSchema = z.strictObject({
  color: z.string(),
  lineWidth: z.number().optional(),
});

export type Path = z.infer<typeof pathSchema>;

export const positionTransformationSchema = z.literal("lla_to_enu").nullable();

export type PositionTransformation = z.infer<
  typeof positionTransformationSchema
>;

export const positionBasePresetSchema = z.enum([
  "lat_lon_alt",
  "east_north_up",
  "north_east_down",
  "forward_right_up",
] as const);

export type PositionBasePreset = z.infer<typeof positionBasePresetSchema>;

export const positionSchema = z.strictObject({
  transformation: positionTransformationSchema.optional(),
  basePreset: positionBasePresetSchema.optional(),
  forward: z.union([topicDataSchema, z.number()]),
  forwardScale: z.number(),
  right: z.union([topicDataSchema, z.number()]),
  rightScale: z.number(),
  up: z.union([topicDataSchema, z.number()]),
  upScale: z.number(),
});

export type Position = z.infer<typeof positionSchema>;

/**
 * An entity's orientation in its parent's frame, as three Euler angles.
 *
 * The hand-authorable mode, and the direct analogue of `positionSchema`: each
 * angle is a constant or a bound topic, with a scale for unit conversion — a
 * topic publishing degrees becomes radians with a scale of `π/180`.
 *
 * `order` is named rather than assumed because no two systems agree on one.
 * URDF's `<origin rpy>` is fixed-axis XYZ, composed as `R = Rz·Ry·Rx`.
 */
export const eulerRotationSchema = z.strictObject({
  type: z.literal("euler"),
  order: z.enum(["XYZ", "XZY", "YXZ", "YZX", "ZXY", "ZYX"] as const).optional(),
  roll: z.union([topicDataSchema, z.number()]),
  rollScale: z.number(),
  pitch: z.union([topicDataSchema, z.number()]),
  pitchScale: z.number(),
  yaw: z.union([topicDataSchema, z.number()]),
  yawScale: z.number(),
});

export type EulerRotation = z.infer<typeof eulerRotationSchema>;

/**
 * A rotation of `angle` about a fixed `axis`.
 *
 * Exactly what a revolute or continuous joint is, which is why an imported robot
 * binds its joints through this mode: the axis comes from the model file and
 * stays constant, and the single bound angle is the joint's position over time.
 *
 * `axis` is stored as authored — a model file need not supply a unit vector —
 * and normalized when read, so an axis of `[0,0,2]` does not turn the angle into
 * double what it says.
 */
export const axisAngleRotationSchema = z.strictObject({
  type: z.literal("axis_angle"),
  axis: z.tuple([z.number(), z.number(), z.number()]),
  angle: z.union([topicDataSchema, z.number()]),
  angleScale: z.number(),
});

export type AxisAngleRotation = z.infer<typeof axisAngleRotationSchema>;

/**
 * An orientation given directly as a quaternion.
 *
 * The mode robot pose topics actually publish, so an odometry or transform
 * message can be bound component by component without converting first. It is
 * also how an import records a node's own orientation, since any rotation is
 * expressible here exactly.
 *
 * No scales: scaling a quaternion's components is not a rotation. The value is
 * normalized on read, so unnormalized data degrades to the nearest rotation
 * rather than distorting the model.
 */
export const quaternionRotationSchema = z.strictObject({
  type: z.literal("quaternion"),
  x: z.union([topicDataSchema, z.number()]),
  y: z.union([topicDataSchema, z.number()]),
  z: z.union([topicDataSchema, z.number()]),
  w: z.union([topicDataSchema, z.number()]),
});

export type QuaternionRotation = z.infer<typeof quaternionRotationSchema>;

/**
 * An entity's orientation relative to its parent.
 *
 * Composes down the hierarchy the way `Position` does — a child's offset is
 * placed by its parent's accumulated rotation — so rotating a robot's shoulder
 * carries its whole arm rather than only re-orienting the shoulder itself.
 *
 * Three modes because one cannot serve all three callers: a person authoring by
 * hand wants angles, a joint is an axis and an angle, and pose topics ship
 * quaternions. They are the same rotation in different clothes; the ECS converts
 * each to a quaternion once, at load, and interpolates by slerp from there.
 */
export const rotationSchema = z.discriminatedUnion("type", [
  eulerRotationSchema,
  axisAngleRotationSchema,
  quaternionRotationSchema,
]);

export type Rotation = z.infer<typeof rotationSchema>;

export const sphereSchema = z.strictObject({
  type: z.literal("sphere"),
  color: z.string(),
  radius: z.number(),
});

export type Sphere = z.infer<typeof sphereSchema>;

export const boxSchema = z.strictObject({
  type: z.literal("box"),
  color: z.string(),
  size: z.number(),
});

export type Box = z.infer<typeof boxSchema>;

/**
 * Geometry taken from one node of a glTF/GLB file.
 *
 * The node supplies shape, material, and orientation; the entity supplies
 * position. That split is what lets an imported robot's joints be re-bound to
 * topic data: the arm's links keep the geometry the export gave them while their
 * placement comes from the entity hierarchy.
 *
 * The node's own rotation and scale — accumulated from its glTF ancestors — are
 * baked onto the drawn mesh at load time, because `positionSchema` carries no
 * orientation. Translation is *not* baked; it becomes the entity's position so it
 * composes through the hierarchy.
 *
 * `nodeName` rather than a node index: an index is meaningless if the file is
 * re-exported, whereas a name usually survives. A name matching no node in the
 * file is reported rather than drawn.
 */
export const modelNodeMeshSchema = z.strictObject({
  type: z.literal("model_node"),
  file: fileSchema,
  nodeName: z.string(),
});

export type ModelNodeMesh = z.infer<typeof modelNodeMeshSchema>;

/**
 * A joint's coupling to another joint: URDF's `<mimic>`.
 *
 * A URDF is a strict tree, so parallel grippers and four-bar linkages are all
 * modelled by cutting the loop and reattaching it here. The coupled joint's
 * value is `multiplier * driver + offset` — an affine function of the joint it
 * follows, evaluated per frame rather than baked in, because the driver's own
 * value may come from a topic.
 *
 * `joint` is a **name**, because that is what identifies a joint everywhere else
 * in this schema. The source document addresses it by index, which is resolved
 * while the document is still in hand — see `articulation.ts`.
 *
 * `multiplier` and `offset` are required rather than optional-with-defaults: the
 * reader supplies URDF's defaults (1 and 0) so no consumer has to remember them.
 */
export const jointMimicSchema = z.strictObject({
  joint: z.string(),
  multiplier: z.number(),
  offset: z.number(),
});

export type JointMimic = z.infer<typeof jointMimicSchema>;

/**
 * The topic and message path a model file declares one of its joints comes from.
 *
 * Read from the source document's `ROBOTO_device` articulation and carried here
 * verbatim: it is what the file *declares*, not what the workspace resolved it
 * to. Resolution against the topics actually open is
 * `ModelLoader/resolveDefaultBindings`, and its result lands in `jointValues`.
 *
 * `topic` and `messagePath` are **split, never joined**. A dot inside a topic
 * name (`/proto/executive.extended_status/context`) or inside a field name must
 * never be read as the boundary between the two — the same lesson
 * `StructuredRequirement` records.
 *
 * `messagePath` is the dot-joined **source-schema** spelling, i.e. what a
 * message path's `path_in_schema` joins to. Deliberately not Roboto's
 * `MessagePathPart[]`: those carry application internals (canonical data types,
 * slice bounds) that have no business in a vendor extension a third party may
 * write.
 *
 * `element` and `elementNamedBy` select one element of an array field and are
 * **mutually exclusive**; neither present means the path is already a scalar.
 * `elementNamedBy` exists because `sensor_msgs/JointState` correlates
 * `position[i]` with `name[i]` *at runtime, by the publisher* — a robot
 * description cannot know `i`, but it does know the joint's name, which is the
 * other half of that correlation.
 *
 * `scale` converts the published value into the extension's SI units — radians
 * for a joint that turns, metres for one that slides. Absent means 1, i.e. the
 * data is already SI. There is no offset and no unit string on purpose: two
 * sources of unit truth invite disagreement, and a consumer only ever applies
 * the number.
 */
export const jointBindingSchema = z.strictObject({
  topic: z.string(),
  messagePath: z.string(),
  element: z.number().int().nonnegative().optional(),
  elementNamedBy: z
    .strictObject({
      /** Source-schema path of the sibling array of names, e.g. `name`. */
      path: z.string(),
      /** Value to look up in that array; its position selects the element. */
      key: z.string(),
    })
    .optional(),
  scale: z.number().optional(),
});

export type JointBinding = z.infer<typeof jointBindingSchema>;

/**
 * One articulated joint of a model, as the panel needs it.
 *
 * `name` is the joint's name in the source robot description, which is *not*
 * always its node's name in the model file — an exporter renames a node that
 * collides with a link. This is the name to show, and the key `jointValues` and
 * `mimic.joint` address it by.
 *
 * `node` is the name of the model node the joint moves: the joint's **child
 * link**, never the joint's own node, which only carries the fixed offset
 * between them. A node name rather than an entity id, because a model is drawn
 * as one component owning its own node hierarchy — there is no entity per link
 * to point at, and a name is what survives into the loaded `AssetContainer`.
 *
 * The binding itself is not here. It lives beside this list as
 * `modelMeshSchema.jointValues`, keyed by joint name; this list says what the
 * joints are, what they may do, and how they are coupled.
 */
export const jointSchema = z.strictObject({
  name: z.string(),
  type: z.enum([
    "revolute",
    "continuous",
    "prismatic",
    "fixed",
    "floating",
    "planar",
  ] as const),
  node: z.string(),
  axis: z.tuple([z.number(), z.number(), z.number()]).optional(),
  lower: z.number().optional(),
  upper: z.number().optional(),
  /**
   * The relation to the joint this one follows, if it is coupled to another.
   *
   * Present means the joint's value is **derived**, and is therefore not a thing
   * to offer a binding for — a coupled joint driven independently would show a
   * gripper with one finger moving.
   */
  mimic: jointMimicSchema.optional(),
  /**
   * What the *file* says this joint is driven by, copied in at import exactly as
   * `axis`, `lower` and `upper` are.
   *
   * A declaration, not a binding: it names a topic that may or may not be open
   * in this workspace. What the joint is actually driven by lives in
   * `modelMeshSchema.jointValues`, and a user's choice there always wins — see
   * the "absent means never decided" rule on `jointValues`.
   */
  defaultBinding: jointBindingSchema.optional(),
});

export type Joint = z.infer<typeof jointSchema>;

/**
 * Where one joint sits over time: a constant, or a bound topic.
 *
 * The direct analogue of one channel of `positionSchema`, and deliberately the
 * same shape — a joint value is one scalar per instant, exactly what a position
 * axis is, so it reuses the same fetch, the same sample-and-hold merge, and the
 * same timeline.
 *
 * `scale` converts units: a topic publishing degrees becomes radians with
 * `π/180`. There is no offset field — a joint's constant term belongs to the
 * model's `mimic` relation, which is read from the file and never authored here.
 */
export const jointValueSchema = z.strictObject({
  value: z.union([topicDataSchema, z.number()]),
  scale: z.number(),
});

export type JointValue = z.infer<typeof jointValueSchema>;

/**
 * A whole model file, drawn as one component.
 *
 * The entity owns a copy of the file's node hierarchy and places it by its own
 * `GlobalPosition`; the joints the file declares move nodes *within* that copy.
 * Two mechanisms, two jobs: the entity hierarchy puts models in the world, and
 * the declared articulation moves parts inside one.
 *
 * That is why the binding surface is `joints`/`jointValues` rather than a
 * per-link entity. For an articulated model a link's pose *is* its joints, and
 * a joint arrives with an axis, a type and limits attached — semantics a raw
 * node transform does not carry.
 */
export const modelMeshSchema = z.strictObject({
  type: z.literal("model"),
  file: fileSchema,
  /**
   * The file the model was generated from, when it was generated from one.
   *
   * A URDF ingested into the platform produces a glTF artifact, and `file` names
   * that artifact. The user chose the URDF, and it is the URDF's name they must
   * see — the generated artifact's path is an implementation detail they never
   * picked and cannot act on. Recording the source here is also what makes
   * staleness detectable: the artifact can be regenerated when its source moves.
   *
   * Absent for a model file chosen directly, where `file` *is* what the user
   * picked.
   */
  source: fileSchema.optional(),
  /**
   * The file's joints, copied in at import rather than re-read from it.
   *
   * So the settings render without fetching and parsing a model, and so a saved
   * workspace still lists its joints when the file has moved or access to it has
   * gone. The cost is a stale copy if the model is re-exported with different
   * kinematics; re-importing is what refreshes it.
   */
  joints: z.array(jointSchema).optional(),
  /**
   * What each joint is driven by, keyed by joint name — the same key `joints`
   * uses, since there is no longer an entity to hang a binding on.
   *
   * Sparse: a joint absent here reads as 0, which is the pose the file was
   * exported in, so an import needs no seeding at all. A joint with a `mimic` is
   * never read from here — its value is derived from the joint it follows.
   *
   * ## Absent means "never decided"
   *
   * Which is what makes `joints[k].defaultBinding` safe to apply: an import
   * binds a joint **if and only if** its key is absent here. A key that is
   * present — bound to a topic or held at a constant — is a decision the user
   * made, and nothing overrides it.
   *
   * The corollary is a trap worth stating: **unbinding a joint writes
   * `{value: 0, scale: 1}`, it does not delete the key.** Deleting it would put
   * the joint back to "never decided", and the file's declared default would
   * silently resurrect the binding the user just removed.
   */
  jointValues: z.record(z.string(), jointValueSchema).optional(),
});

export type ModelMesh = z.infer<typeof modelMeshSchema>;

export const meshSchema = z.discriminatedUnion("type", [
  sphereSchema,
  boxSchema,
  modelNodeMeshSchema,
  modelMeshSchema,
]);

export type Mesh = z.infer<typeof meshSchema>;

const vectorFieldDisplayModeSchema = z.enum([
  "field_only",
  "current_only",
] as const);

export type VectorFieldDisplayMode = z.infer<
  typeof vectorFieldDisplayModeSchema
>;

export const vectorFieldSchema = z.strictObject({
  forward: z.union([topicDataSchema, z.number()]),
  forwardScale: z.number(),
  right: z.union([topicDataSchema, z.number()]),
  rightScale: z.number(),
  up: z.union([topicDataSchema, z.number()]),
  upScale: z.number(),
  color: z.string(),
  lineWidth: z.number().gt(0),
  stride: z.number().gte(1).optional(),
  displayMode: vectorFieldDisplayModeSchema.optional(),
  gradientEnabled: z.boolean().optional(),
  colorLow: z.string().optional(),
  colorHigh: z.string().optional(),
});

export type VectorField = z.infer<typeof vectorFieldSchema>;

export const gridSchema = z.strictObject({
  color: z.string(),
  scale: z.number().optional(),
  verticalOffset: z.number().optional(),
});

export type Grid = z.infer<typeof gridSchema>;

/**
 * An entity's declaration: its existence, and its place in the hierarchy.
 *
 * Nothing else. Following ECS parlance, an entity is an id and a thin wrapper
 * around whatever components are attached to it — it carries no data of its own.
 * The components are keyed by the same id in `PanelData.entities`.
 *
 * This is the same shape the `entityHierarchy` helpers read, so the registry is
 * stored in the form its consumers want rather than being adapted at each use.
 */
export const entityDeclarationSchema = z.strictObject({
  parent: z.string().optional(),
});

export type EntityDeclaration = z.infer<typeof entityDeclarationSchema>;

/**
 * The panel's entities: which exist, and which is parented to which.
 *
 * A root is an entry with no `parent`. An entity with no components at all is
 * still a legitimate entry — a structural node whose only job is to give its
 * children a frame, which is exactly what a glTF's link nodes are.
 */
export const entityRegistrySchema = z.record(
  z.string(),
  entityDeclarationSchema,
);

export type EntityRegistry = z.infer<typeof entityRegistrySchema>;

/** The components attached to one entity. */
export const entitySchema = z.strictObject({
  id: z.string(),
  position: positionSchema.optional(),
  rotation: rotationSchema.optional(),
  mesh: meshSchema.optional(),
  path: pathSchema.optional(),
  vectorField: vectorFieldSchema.optional(),
});

export type Entity = z.infer<typeof entitySchema>;

export type EntityID = string;

export const threeDPanelConfigSchema = z.strictObject({
  camera: cameraSchema.optional(),
  grid: gridSchema.optional(),
  /**
   * The entity registry: which entities the panel has, and their hierarchy.
   *
   * Config rather than data because an entity is a declaration, not data. A
   * layout stores every panel field except `data`, and rebuilds `data.entities`
   * by matching rules against the workspace's topics — so an entity whose
   * components bind no topic would simply cease to exist. Declaring entities here
   * means the skeleton is restored whatever the rules match, which is what makes
   * an imported glTF hierarchy stable across a Layout round-trip.
   *
   * Cycles — including an entity parented to itself — are rejected: no ordering
   * of the entities would let a child's absolute position be accumulated from its
   * parent's. A `parent` naming an entity absent from the registry is *accepted*:
   * one stale reference must not stop a whole workspace from opening.
   * `resolveParent` reads it as a root and the sidebar flags it.
   *
   * At least one entity, mirroring what the panel has always required.
   */
  entities: entityRegistrySchema
    .refine(
      (entities) => Object.keys(entities).length >= 1,
      "entities must have at least 1 entry",
    )
    .refine(
      (entities) => findParentCycle(entities) === null,
      "entity parents must not form a cycle",
    )
    .optional(),
});

export type PanelConfig = z.infer<typeof threeDPanelConfigSchema>;

export const threeDPanelDataSchema = z.strictObject({
  entities: z
    .record(z.string(), entitySchema)
    .refine(
      (v) => Object.keys(v).length >= 1,
      "entities must have at least 1 entry",
    ),
});

export type PanelData = z.infer<typeof threeDPanelDataSchema>;
